import { useMemo } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { useI18n } from '../context/I18nContext';

/**
 * Custom hook for managing message details logic
 * Extracts sender information and read receipt processing
 * 
 * @param {Object} message - Matrix message event object
 * @param {Array} members - List of room members
 * @param {string} roomId - The room ID
 * @returns {Object} Processed message details data
 */
const useMessageDetails = (message, members, roomId) => {
  const { t } = useI18n();
  const client = useMatrixClient();
  const currentUserId = client?.getUserId();
  const room = client?.getRoom(roomId);

  // Get message metadata
  const messageId = message?.getId?.() || message?.eventId || '';
  const senderId = message?.getSender?.() || message?.sender || '';
  const timestamp = message?.getTs?.() || message?.timestamp || Date.now();

  // Get message content and check for attachments
  const messageContent = useMemo(() => {
    return message?.getContent?.() || message?.content || {};
  }, [message]);

  const isAttachment = useMemo(() => {
    const msgtype = messageContent?.msgtype;
    return msgtype && ['m.file', 'm.image', 'm.video', 'm.audio'].includes(msgtype);
  }, [messageContent]);

  const attachmentName = useMemo(() => {
    if (!isAttachment) return null;
    return messageContent?.body || messageContent?.filename || 'Unnamed file';
  }, [isAttachment, messageContent]);

  // Find sender in members list
  const sender = useMemo(() => {
    return members.find(m => m.id === senderId || m.userId === senderId);
  }, [members, senderId]);

  // Get display name for sender (handles "You" for current user)
  const senderDisplayName = useMemo(() => {
    if (senderId === currentUserId) {
      return t('MessageDetailsView.you');
    }
    return sender?.name || senderId;
  }, [sender, senderId, currentUserId, t]);

  // Separate members into those who read and those who didn't
  const { readByMembers, notReadByMembers } = useMemo(() => {
    const readBy = [];
    const notReadBy = [];

    if (!room || !messageId) {
      return { readByMembers: readBy, notReadByMembers: notReadBy };
    }

    members.forEach(member => {
      const memberId = member.id || member.userId;
      
      // Always skip the sender (they've implicitly "read" their own message by sending it)
      if (memberId === senderId) {
        return;
      }

      // Check if this member has read this message or any message after it
      // room.hasUserReadEvent returns true if the user has read up to or past this event
      const hasRead = room.hasUserReadEvent(memberId, messageId);
      
      if (hasRead) {
        readBy.push(member);
      } else {
        notReadBy.push(member);
      }
    });

    return {
      readByMembers: readBy,
      notReadByMembers: notReadBy,
    };
  }, [room, members, messageId, senderId]);

  return {
    messageId,
    senderId,
    timestamp,
    sender,
    senderDisplayName,
    readByMembers,
    notReadByMembers,
    isAttachment,
    attachmentName,
  };
};

export default useMessageDetails;
